import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { AuthToken, LoginDTO, UserDTO, UsuarioNewPass } from '../models/auth.models';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AlterarSenha } from 'src/app/pages/change-password/_models/senha.model';

const TOKEN_KEY = 'auth-token';
const USER_KEY = 'auth-user';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  baseURL = `${environment.apiUrl}/usuario`;

  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  public currentUser(): AuthToken | null {
    return this.getDecodedAccessToken();
  }

  login(user: LoginDTO): Observable<{ user: UserDTO, access_token: string }> {
    return this.http.post<{ user: UserDTO, access_token: string }>(`${this.baseURL}/auth`, user);
  }

  resetPassword(user: LoginDTO): Observable<{ user: UserDTO, access_token: string }> {
    return this.http.post<{ user: UserDTO, access_token: string }>(`${this.baseURL}/recovery`, user);
  }

  updatePassword(user: UsuarioNewPass): Observable<{ user: UserDTO }> {
    return this.http.put<{ user: UserDTO }>(`${this.baseURL}/updatepass`, user);
  }

  alterarSenha(dto: AlterarSenha): Observable<any> {
    return this.http.put(`${this.baseURL}/updatepassconfirm`, dto);
  }

  logout(): void {
    window.sessionStorage.clear();
    this.router.navigate(['/account/login']);
  }

  public saveToken(token: string): void {
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.setItem(TOKEN_KEY, token);
  }

  public saveUser(user: UserDTO): void {
    window.sessionStorage.removeItem(USER_KEY);
    window.sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  public getStoredUser(): UserDTO | null {
    const user = window.sessionStorage.getItem(USER_KEY);

    if (!user) {
      return null;
    }

    try {
      return JSON.parse(user) as UserDTO;
    } catch {
      return null;
    }
  }

  public getToken(key: string = TOKEN_KEY): string | null {
    return window.sessionStorage.getItem(key);
  }

  public decodeResetToken(token: string): { id: string, unique_name: string } | null {
    try {
      return jwt_decode(token);
    } catch {
      return null;
    }
  }

  getDecodedAccessToken(): AuthToken | null {
    const token = this.getToken();

    if (!token) {
      return null;
    }

    try {
      return jwt_decode<AuthToken>(token);
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getDecodedAccessToken();

    if (!token || !token.exp) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return token.exp > now;
  }

  getUserRole(): number | null {
    const token = this.getDecodedAccessToken();
    const storedUser = this.getStoredUser();

    const role =
      token?.cod_grupo_usuario_gru ??
      storedUser?.cod_grupo_usuario_gru ??
      token?.role;

    if (role === undefined || role === null) {
      return null;
    }

    return Number(role);
  }

  getUserName(): string {
    const token = this.getDecodedAccessToken();
    const storedUser = this.getStoredUser();
    return token?.unique_name || storedUser?.des_nome_usu || '';
  }

  getClientName(): string {
    const token = this.getDecodedAccessToken();
    const storedUser = this.getStoredUser();
    const tokenData = token as AuthToken & Record<string, any>;
    const storedUserData = storedUser as UserDTO & Record<string, any>;

    return (
      tokenData?.des_cliente_cli ||
      tokenData?.des_nome_cli ||
      tokenData?.des_empresa_emp ||
      tokenData?.nome_cliente ||
      tokenData?.nome_empresa ||
      storedUserData?.des_cliente_cli ||
      storedUserData?.des_nome_cli ||
      storedUserData?.des_empresa_emp ||
      storedUserData?.nome_cliente ||
      storedUserData?.nome_empresa ||
      ''
    );
  }

  getUserId(): number | null {
    const token = this.getDecodedAccessToken();

    if (!token?.cod_usuario_usu) {
      return null;
    }

    return Number(token.cod_usuario_usu);
  }

  getPartnerId(): number | null {
    const token = this.getDecodedAccessToken();
    const storedUser = this.getStoredUser();
    const partnerId = token?.cod_parceiro_par ?? storedUser?.cod_parceiro_par;

    if (!partnerId) {
      return null;
    }

    return Number(partnerId);
  }

  isSuperAdmin(): boolean {
    return (
      this.getDecodedAccessToken()?.bit_admin_usu === true ||
      this.getStoredUser()?.bit_admin_usu === true
    );
  }

  isAdmin(): boolean {
    return this.getUserRole() === 1;
  }

  hasRole(roles: number[]): boolean {
    const userRole = this.getUserRole();

    if (userRole === null) {
      return false;
    }

    return roles.includes(userRole);
  }

  hasPermission(permission: string): boolean {
    return this.getPermissions().includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    const userPermissions = this.getPermissions();
    return permissions.some(permission => userPermissions.includes(permission));
  }

  getPermissions(): string[] {
    const role = this.getUserRole();

    switch (role) {
      case 1:
        return [
          'DASHBOARD_VIEW',
          'FILES_VIEW',
          'GROUP_USER_VIEW',
          'GROUP_USER_EDIT'
        ];

      case 2:
        return [
          'DASHBOARD_VIEW',
          'FILES_VIEW'
        ];

      default:
        return [];
    }
  }
}
